import { useEffect, useRef, useState } from 'react';
import { useApiClient } from '../../../utils/apiClient';
import { useAuth, usePermissions } from '../../../contexts/AuthContext';
import { commonDataService } from '../../../services/commonDataService';
import {
  scopedRevenueSummaryUrl,
  type ScopedRevenueSummary,
} from '../../../services/scopedRevenueSummary';
import { hasPermission, P } from '../../../utils/permissions';
import {
  consultantRoleService,
  isConsultantRoleSync,
} from '../../../services/consultantRoleService';
import {
  dashboardDataService,
  clearDashboardDataCache,
} from '../../../services/dashboardDataService';
import type {
  ActiveProjectsConfig as ActiveProjectsScopeConfig,
  CachedMappings,
  Consultant,
  DashboardConfig,
  DateRangeFilter,
  TeamUtilisation,
} from '../types';
import { initializeDateRangeFilter, saveDateRangeFilter } from '../utils/dateRange';
import { normalizeActiveProjectsConfig } from '../utils/dashboardConfig';
import { mergeConsultantUtilisation } from './mergeConsultantUtilisation';

/**
 * Single data + state hook for the dashboard. Owns every fetch, every piece
 * of derived state, and the date-range filter. The shell (`Dashboard.tsx`)
 * and the section components consume the returned bag and render JSX only.
 *
 * Extracted verbatim from the legacy 1765-line `Dashboard.tsx` as part of
 * Feature 02 Phase 1: zero logic change. Component-scope predicate closures
 * were lifted to pure helpers in `utils/`; the dependency arrays, fetch
 * choreography, and side effects are preserved.
 */
export interface UseDashboardDataResult {
  // raw data
  consultants: Consultant[];
  teamUtilisation: TeamUtilisation | null;
  /**
   * The Financials tiles, the pipeline panel and the at-risk alert, worked out
   * on the server for this range and the workspace's dashboard configuration
   * (AC-59). `null` until it lands, or for a reader without `view:projects`.
   */
  revenueSummary: ScopedRevenueSummary | null;
  // status
  loading: boolean;
  error: string | null;
  lastUpdated: Date;
  // config
  dashboardConfig: DashboardConfig;
  setDashboardConfig: React.Dispatch<React.SetStateAction<DashboardConfig>>;
  availableStages: string[];
  saveDashboardConfig: (config: DashboardConfig, shouldRefreshData?: boolean) => Promise<void>;
  reloadDashboardConfig: () => Promise<DashboardConfig>;
  // date range
  dateRangeFilter: DateRangeFilter;
  handleDateRangeChange: (filter: DateRangeFilter) => void;
  // bindings
  isConsultantRole: (jobTitle: string) => boolean;
  // actions
  refresh: () => void;
}

export function useDashboardData(): UseDashboardDataResult {
  const { getAccessToken } = useAuth();
  const { permissions } = usePermissions();
  const apiClient = useApiClient(getAccessToken);

  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<ScopedRevenueSummary | null>(null);
  const [teamUtilisation, setTeamUtilisation] = useState<TeamUtilisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Track component initialization (preserved from legacy implementation —
  // dedupe now lives at the network layer but the refs are kept for parity).
  const isInitialized = useRef(false);
  const mountCount = useRef(0);

  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig>({
    revenueCalculationMethod: 'EITHER_DATE',
    pipelineStages: ['Discovery', 'Proposal', 'Negotiation', 'Active'],
    projectPipelineStages: ['Discovery', 'Proposal', 'Negotiation', 'Active'],
    activeProjectsConfig: { excludeCompleted: true, selectedStages: [] },
  });

  const [availableStages, setAvailableStages] = useState<string[]>([]);

  // Active Projects Configuration (legacy — kept verbatim; dashboardConfig is
  // the live source. Reading localStorage on init has no side-effect beyond
  // a one-time parse, matching original behaviour).
  const [, setActiveProjectsConfig] = useState<ActiveProjectsScopeConfig>(() => {
    const saved = localStorage.getItem('dashboard_activeProjectsConfig');
    try {
      const parsed = saved ? JSON.parse(saved) : null;
      if (parsed && (parsed.excludeStages || parsed.includeOnlyStages)) {
        return {
          excludeCompleted: parsed.excludeCompleted || true,
          selectedStages: parsed.includeOnlyStages || [],
        };
      }
      return parsed || { excludeCompleted: true, selectedStages: [] };
    } catch (e) {
      console.warn('Failed to parse saved active projects config:', e);
      return { excludeCompleted: true, selectedStages: [] };
    }
  });
  void setActiveProjectsConfig; // silence unused-setter warning; init side-effect preserved

  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>(
    initializeDateRangeFilter(),
  );

  // Cached job-title → department mapping (used in legacy code paths;
  // preserved for parity — no downstream consumer reads it today).
  const [, setCachedMappings] = useState<CachedMappings>({ jobTitleToDepartment: {} });

  const [consultantRoleConfig, setConsultantRoleConfig] = useState<{
    [jobTitle: string]: boolean;
  }>({});

  // Only persist the preset type for relative ranges so they recompute against
  // today on next load; hydrate custom ranges from the stored ISO boundaries.
  // Shared helper, defaulting to the `dashboard_*` storage keys.
  const handleDateRangeChange = (filter: DateRangeFilter) => {
    setDateRangeFilter(filter);
    saveDateRangeFilter(filter);
  };

  const loadDashboardConfig = async () => {
    try {
      const config = await dashboardDataService.config.get(apiClient);
      const normalizedConfig = {
        ...config,
        activeProjectsConfig: normalizeActiveProjectsConfig(config.activeProjectsConfig),
      };
      setDashboardConfig(normalizedConfig);
    } catch (error) {
      console.error('Error loading dashboard config:', error);
    }
  };

  /**
   * Saves the org-wide configuration. A failure throws: the tile dialog shows
   * it, or merges a stale refusal in place (docs/architecture/concurrent-edits.md).
   */
  const saveDashboardConfig = async (
    config: DashboardConfig,
    shouldRefreshData = true,
  ) => {
    const savedConfig = await apiClient.post('/api/dashboard-config', {
      revenueCalculationMethod: config.revenueCalculationMethod,
      pipelineStages: config.pipelineStages,
      projectPipelineStages: config.projectPipelineStages,
      activeProjectsConfig: normalizeActiveProjectsConfig(config.activeProjectsConfig),
      ...(config.version != null ? { version: config.version } : {}),
    });
    setDashboardConfig({
      ...savedConfig,
      activeProjectsConfig: normalizeActiveProjectsConfig(savedConfig.activeProjectsConfig),
    });

    // The server reads the saved configuration to scope the Financials
    // figures, so any change to it asks for them again.
    if (shouldRefreshData) {
      clearDashboardDataCache();
      fetchDashboardData();
    }
  };

  /** Re-reads the org-wide configuration, past the dedupe cache, for a stale-edit merge. */
  const reloadDashboardConfig = async (): Promise<DashboardConfig> => {
    dashboardDataService.config.clear();
    const config = await dashboardDataService.config.get(apiClient);
    const normalised = {
      ...config,
      activeProjectsConfig: normalizeActiveProjectsConfig(config.activeProjectsConfig),
    };
    setDashboardConfig(normalised);
    return normalised;
  };

  const fetchMappings = async () => {
    try {
      const jobTitleMappings = (await dashboardDataService.jobTitleDepartmentMappings.get(
        apiClient,
      )) as any[];

      setCachedMappings({
        jobTitleToDepartment: jobTitleMappings.reduce((acc: any, m: any) => {
          acc[m.jobTitle] = m.department;
          return acc;
        }, {}),
      });
    } catch (error) {
      console.error('Error fetching mappings:', error);
    }
  };

  const fetchConsultantRoleConfig = async () => {
    try {
      const config = await consultantRoleService.getConsultantRoleConfig(apiClient);
      setConsultantRoleConfig(config);
    } catch (error) {
      console.error('Error fetching consultant role config:', error);
      setConsultantRoleConfig({});
    }
  };

  // Consolidated function to fetch all dashboard data.
  // Concurrency control lives at the network layer — `dashboardDataService`
  // coalesces concurrent callers onto a single in-flight promise and serves
  // repeat callers from a 60s TTL cache, so StrictMode double-invocation and
  // transient remounts collapse to one HTTP request per endpoint.
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      await Promise.all([
        loadDashboardConfig(),
        fetchMappings(),
        fetchConsultantRoleConfig(),
      ]);

      try {
        const stagesData = await dashboardDataService.projectStagesAll.get(apiClient);
        const stages = Array.isArray(stagesData) ? stagesData : [];
        setAvailableStages(stages.map((stage: any) => stage.name).filter(Boolean));
      } catch (error) {
        console.error('Error fetching all stages:', error);
      }

      // Spelt exactly as Reports spells it (`useReportsData`): the shared cache keys on the raw URL.
      const utilisationRange = new URLSearchParams({
        startDate: dateRangeFilter.startDate.toISOString(),
        endDate: dateRangeFilter.endDate.toISOString(),
      });
      const currentUtilisationUrl = `/planning/team/utilisation?${utilisationRange}`;
      // The Financials tiles and the at-risk alert open with `view:projects`;
      // their money is stripped on the server without `view:project:value`.
      const canReadBook = hasPermission(permissions, P.VIEW_PROJECTS);

      const [
        employeesResult,
        teamUtilisationResult,
        revenueSummaryResult,
      ] = await Promise.allSettled([
        dashboardDataService.employees.get(apiClient),
        dashboardDataService.teamUtilisation.get(apiClient, currentUtilisationUrl),
        canReadBook
          ? commonDataService.scopedRevenueSummary.get(
              apiClient,
              scopedRevenueSummaryUrl('dashboard', dateRangeFilter),
            )
          : Promise.resolve(null),
      ]);

      if (revenueSummaryResult.status === 'fulfilled') {
        setRevenueSummary(revenueSummaryResult.value);
      } else {
        console.error('Failed to fetch the revenue summary:', revenueSummaryResult.reason);
        // The previous range's figures must not sit under the new range's label.
        setRevenueSummary(null);
        setError('Some dashboard figures could not be loaded.');
      }

      let employeesFulfilled = false;
      if (employeesResult.status === 'fulfilled') {
        employeesFulfilled = true;
        const employeesData = employeesResult.value as any;
        const employees =
          employeesData?.data || employeesData?.employees || employeesData || [];

        const mappedConsultants: Consultant[] = employees.map((emp: any) => ({
          id: emp.id,
          firstName: emp.firstName || emp.first_name || '',
          lastName: emp.lastName || emp.last_name || '',
          email: emp.email || '',
          department: emp.department || 'Unknown',
          jobTitle: emp.position || emp.positionDisplayName || 'Unknown Position',
          status: emp.status || 'active',
          avatarUrl: emp.avatarUrl,
          displayName: emp.displayName ?? null,
          utilisation: emp.utilisation || 0,
          revenue: emp.revenue || 0,
        }));

        setConsultants(mappedConsultants);
      } else {
        console.error('Failed to fetch employees:', employeesResult.reason);
      }

      let currentUtilisationData: any = null;
      if (teamUtilisationResult.status === 'fulfilled') {
        currentUtilisationData = teamUtilisationResult.value;
        setTeamUtilisation(currentUtilisationData);
      } else {
        console.error(
          'Failed to fetch current period team utilisation:',
          teamUtilisationResult.reason,
        );
      }

      // Merge utilisation (+ leave context) onto consultants once both employees
      // and team utilisation have resolved.
      if (currentUtilisationData && employeesFulfilled) {
        setConsultants((prevConsultants) =>
          mergeConsultantUtilisation(prevConsultants, currentUtilisationData),
        );
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Consolidated useEffect for fetching data — only runs when dateRangeFilter changes or on mount.
  useEffect(() => {
    mountCount.current += 1;
    // Always fetch; dedupe lives in `dashboardDataService` at the network
    // layer, so a StrictMode double-invoke or transient remount collapses to
    // one HTTP request per endpoint. Date range changes still trigger fresh
    // utilisation calls because the URL (and therefore the cache key) differs.
    fetchDashboardData();

    if (!isInitialized.current) {
      isInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeFilter.startDate.getTime(), dateRangeFilter.endDate.getTime()]);

  const isConsultantRole = (jobTitle: string): boolean =>
    isConsultantRoleSync(jobTitle, consultantRoleConfig);

  const refresh = () => {
    clearDashboardDataCache();
    fetchDashboardData();
  };

  return {
    consultants,
    teamUtilisation,
    revenueSummary,
    loading,
    error,
    lastUpdated,
    dashboardConfig,
    setDashboardConfig,
    availableStages,
    saveDashboardConfig,
    reloadDashboardConfig,
    dateRangeFilter,
    handleDateRangeChange,
    isConsultantRole,
    refresh,
  };
}
